// Paneles alimentados por el complemento OCDS de Mercado Público
// (foro de consultas, oferentes y acto de adjudicación).
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MessageSquare, Users, Gavel, ExternalLink, Database } from "lucide-react";
import type { Licitacion } from "@/types/licitacion";
import { formatMonto, normalizeMoneda } from "@/lib/monto";

const fmtFecha = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("es-CL", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "America/Santiago",
      })
    : "—";

/** Indica de qué fuente(s) proviene la ficha (API v1 y/o OCDS). */
export function FuenteDatosBadge({ licitacion }: { licitacion: Licitacion }) {
  const fuente = licitacion.fuenteDatos ?? "api_v1";
  const label =
    fuente === "api_v1+ocds"
      ? "API v1 + OCDS"
      : fuente === "ocds"
        ? "Solo OCDS"
        : "Solo API v1";
  return (
    <TooltipProvider>
      <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant={licitacion.tieneOcds ? "secondary" : "outline"} className="gap-1">
          <Database className="h-3 w-3" aria-hidden />
          {label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        {licitacion.tieneOcds
          ? `Datos cruzados con el estándar OCDS${
              licitacion.ocdsSyncedAt ? ` el ${fmtFecha(licitacion.ocdsSyncedAt)}` : ""
            }.`
          : "Aún sin cruce OCDS: la ficha proviene solo de la API REST v1."}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function Vacio({ texto }: { texto: string }) {
  return <p className="text-sm text-muted-foreground">{texto}</p>;
}

/** Foro de consultas y respuestas (OCDS `tender.enquiries`). */
export function PreguntasRespuestasPanel({ licitacion }: { licitacion: Licitacion }) {
  const qa = licitacion.preguntasRespuestas ?? [];
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquare className="h-4 w-4 text-muted-foreground" aria-hidden />
          Foro de consultas
          {qa.length > 0 && <Badge variant="secondary">{qa.length}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {qa.length === 0 ? (
          <Vacio texto="Sin consultas publicadas para esta licitación." />
        ) : (
          qa.map((item, i) => (
            <div key={item.id ?? i} className="rounded-lg border border-border p-3">
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-medium">{item.pregunta ?? "Consulta sin texto"}</p>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {fmtFecha(item.fecha)}
                </span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                {item.respuesta ?? "Aún sin respuesta del organismo."}
              </p>
              {item.fechaRespuesta && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Respondida el {fmtFecha(item.fechaRespuesta)}
                </p>
              )}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

/** Proveedores que presentaron oferta (OCDS `tender.tenderers`). */
export function OferentesPanel({ licitacion }: { licitacion: Licitacion }) {
  const oferentes = licitacion.oferentes ?? [];
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="h-4 w-4 text-muted-foreground" aria-hidden />
          Oferentes
          {oferentes.length > 0 && <Badge variant="secondary">{oferentes.length}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {oferentes.length === 0 ? (
          <Vacio texto="Sin oferentes publicados (se informan tras el cierre de ofertas)." />
        ) : (
          <ul className="divide-y divide-border">
            {oferentes.map((o, i) => (
              <li key={o.idMp ?? i} className="flex items-center justify-between gap-3 py-2">
                <span className="text-sm">{o.nombre ?? "Proveedor sin nombre"}</span>
                <span className="font-mono text-xs text-muted-foreground">{o.rut ?? "—"}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/** Resumen del acto de adjudicación (OCDS `awards[]`). */
export function AdjudicacionOcdsPanel({ licitacion }: { licitacion: Licitacion }) {
  const adj = licitacion.adjudicacionResumen;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Gavel className="h-4 w-4 text-muted-foreground" aria-hidden />
          Adjudicación (OCDS)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!adj ? (
          <Vacio texto="La licitación aún no registra un acto de adjudicación en OCDS." />
        ) : (
          <>
            <dl className="grid gap-3 sm:grid-cols-3">
              <div>
                <dt className="text-xs text-muted-foreground">Estado</dt>
                <dd className="text-sm font-medium">{adj.estado ?? "—"}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Fecha</dt>
                <dd className="text-sm font-medium">{fmtFecha(adj.fecha)}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Monto adjudicado</dt>
                <dd className="text-sm font-medium">
                  {adj.monto != null ? formatMonto(adj.monto, normalizeMoneda(adj.moneda)) : "—"}
                </dd>
              </div>
            </dl>
            {adj.proveedores.length > 0 && (
              <div>
                <p className="mb-1 text-xs text-muted-foreground">Proveedores adjudicados</p>
                <ul className="space-y-1">
                  {adj.proveedores.map((p, i) => (
                    <li key={p.idMp ?? i} className="text-sm">
                      {p.nombre ?? "—"}{" "}
                      <span className="font-mono text-xs text-muted-foreground">
                        {p.rut ?? ""}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {adj.documentos.length > 0 && (
              <div>
                <p className="mb-1 text-xs text-muted-foreground">Documentos</p>
                <ul className="space-y-1">
                  {adj.documentos.map((d, i) => (
                    <li key={d.url ?? i}>
                      <a
                        href={d.url ?? "#"}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" aria-hidden />
                        {d.titulo ?? d.tipo ?? "Documento"}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
